import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useTeamBoardTasks } from "../../../hooks/useTeamBoards";
import { formatDateTimePtBR } from "../../../utils/dateTime";
import TeamTasksLoadError from "./TeamTasksLoadError";
import { formatTaskDisplayName } from "../utils/taskDisplayName";

const TITLE_KEYS = {
  open: "boardListSummaryTasksTitleOpen",
  all: "boardListSummaryTasksTitleTotal",
  overdue: "boardListSummaryTasksTitleOverdue",
  unassigned: "boardListSummaryTasksTitleUnassigned",
};

function taskSearchText(task) {
  return [
    task?.id,
    formatTaskDisplayName(task?.id, task?.title),
    task?.boardName,
    task?.columnName,
    ...(Array.isArray(task?.assigneeNames) ? task.assigneeNames : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase("pt-BR");
}

function isTaskDone(task) {
  return Boolean(task?.columnIsDone || task?.completedAt);
}

const BoardSummaryTasksDialog = ({ open, kind, onClose, onOpenTask }) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const [query, setQuery] = useState("");
  const summary =
    kind === "open" || kind === "all" || kind === "overdue" || kind === "unassigned" ? kind : null;

  const { data, isLoading, isError, error, refetch, isFetching } = useTeamBoardTasks(
    { summary, limit: 2000 },
    { enabled: Boolean(open && summary) }
  );

  const tasks = data?.tasks || [];
  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("pt-BR");
    if (!q) return tasks;
    return tasks.filter((task) => taskSearchText(task).includes(q));
  }, [query, tasks]);

  const handleClose = () => {
    setQuery("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" fontWeight={800}>
          {summary ? t(TITLE_KEYS[summary]) : ""}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("boardListSummaryTasksHint")}
        </Typography>
        <IconButton
          aria-label={t("closeCard")}
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("boardListSummaryTasksSearch")}
            fullWidth
          />

          {data?.truncated ? (
            <Typography variant="caption" color="text.secondary">
              {t("boardListSummaryTasksTruncated", { count: tasks.length })}
            </Typography>
          ) : null}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {isError ? (
            <TeamTasksLoadError error={error} onRetry={() => refetch()} isRetrying={isFetching} />
          ) : null}

          {!isLoading && !isError && filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
              {query.trim() ? t("boardListSummaryTasksEmptySearch") : t("boardListSummaryTasksEmpty")}
            </Typography>
          ) : null}

          {!isLoading && !isError && filtered.length > 0 ? (
            <TableContainer sx={{ maxHeight: "min(62vh, 640px)" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("boardListSummaryTasksColTitle")}</TableCell>
                    <TableCell>{t("boardListSummaryTasksColBoard")}</TableCell>
                    <TableCell>{t("boardListSummaryTasksColColumn")}</TableCell>
                    <TableCell>{t("boardListSummaryTasksColAssignee")}</TableCell>
                    <TableCell>{t("boardListSummaryTasksColDue")}</TableCell>
                    <TableCell>{t("boardListSummaryTasksColCreated")}</TableCell>
                    <TableCell>{t("boardListSummaryTasksColStatus")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((task) => {
                    const done = isTaskDone(task);
                    const overdue =
                      !done &&
                      task.dueAt &&
                      !Number.isNaN(new Date(task.dueAt).getTime()) &&
                      new Date(task.dueAt) < new Date();
                    const assignees = Array.isArray(task.assigneeNames) && task.assigneeNames.length
                      ? task.assigneeNames.join(", ")
                      : t("inboxUnassignedLabel");
                    return (
                      <TableRow
                        key={task.id}
                        hover
                        tabIndex={0}
                        onClick={() => onOpenTask?.(task)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onOpenTask?.(task);
                          }
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ fontWeight: 700, maxWidth: 280 }}>
                          {formatTaskDisplayName(task.id, task.title) || "—"}
                        </TableCell>
                        <TableCell>{task.boardName || "—"}</TableCell>
                        <TableCell>{task.columnName || "—"}</TableCell>
                        <TableCell>{assignees}</TableCell>
                        <TableCell
                          sx={{
                            color: overdue ? "warning.dark" : "text.primary",
                            fontWeight: overdue ? 700 : 400,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {task.dueAt ? formatDateTimePtBR(task.dueAt) : "—"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDateTimePtBR(task.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={done ? t("boardListSummaryTasksStatusDone") : t("boardListSummaryTasksStatusOpen")}
                            sx={{
                              fontWeight: 700,
                              bgcolor: done
                                ? alpha(theme.palette.success.main, 0.12)
                                : alpha(theme.palette.primary.main, 0.1),
                              color: done ? "success.dark" : "primary.main",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default BoardSummaryTasksDialog;
